/**
 * THE PAGE A ROUTE'S GUARD STANDS IN FRONT OF (D-92 part 3, Miles 2026-10-24: "no matter what the process/transition …
 * and not matter what mode the user is in").
 *
 * A route's guard (the sign-in check, a developer or permission gate, the justice page's gate) shows a loading screen
 * while it decides, in front of the page it leads to. That screen was today's, noon only (D-66), so a person at dusk
 * opening a page of the new design saw a noon screen first: on every full load of a signed-in page, and on the way in
 * from a public page. The guard can tell which page it leads to without drawing it: the element it was handed is the
 * page's own, perhaps inside another guard, and withPavilion (./pavilion/withPavilion.tsx) marks a page of the new
 * design with its key and its chrome (withSafeAnimation carries the mark to its wrapper). The guard's loading screen
 * (src/components/routing/GuardLoading.tsx) then waits in the light that page opens in, and stays noon in front of one
 * of today's pages.
 *
 * On the first download (App.tsx's guards read it), so it imports only React and a type.
 */
import React from 'react';
import type { PavilionPageKey } from './pavilion/shell/pageRegistry';

/** What withPavilion marks a page of the new design with. */
export interface PavilionPageMark {
  /** The page's key: its surface word (config/site-design.json) and its registry row. */
  readonly key: PavilionPageKey;
  /** The page stands under the app header (its registry row's chrome 'app'). */
  readonly appChrome: boolean;
}

/** The static property of a page's component the mark rides on. */
export const PAVILION_PAGE_PROP = 'pavilionPage';

/** How many wrappers deep the walk looks (App.tsx nests two guards at most). */
const MAX_DEPTH = 4;

/** The mark a component carries, or null: anything malformed is no mark. */
function markOf(type: unknown): PavilionPageMark | null {
  if (typeof type !== 'function' && (typeof type !== 'object' || type === null)) return null;
  const mark = (type as Record<string, unknown>)[PAVILION_PAGE_PROP] as Partial<PavilionPageMark> | null | undefined;
  return mark && typeof mark.key === 'string' && typeof mark.appChrome === 'boolean' ? (mark as PavilionPageMark) : null;
}

/** Mark a component as a page of the new design (withPavilion does). */
export function markPavilionPage<C extends object>(component: C, mark: PavilionPageMark): C & { pavilionPage: PavilionPageMark } {
  return Object.assign(component, { [PAVILION_PAGE_PROP]: mark }) as C & { pavilionPage: PavilionPageMark };
}

/** Carry a page's mark from a component to a wrapper around it (withSafeAnimation does); nothing when it has none. */
export function carryPavilionPage(from: unknown, to: object): void {
  const mark = markOf(from);
  if (mark) markPavilionPage(to, mark);
}

/** The page of the new design `node` leads to, through wrappers that each hold one element; else null. */
export function pavilionPageAhead(node: React.ReactNode, depth = 0): PavilionPageMark | null {
  if (depth > MAX_DEPTH || !React.isValidElement(node)) return null;
  const mark = markOf(node.type);
  if (mark) return mark;
  const children = (node.props as { children?: React.ReactNode }).children;
  return React.isValidElement(children) ? pavilionPageAhead(children, depth + 1) : null;
}
